import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Play, Pause, RotateCcw, Volume2, Settings, Zap } from 'lucide-react';
import { Slider } from './ui/slider';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const SynchronizedTextReader = ({ 
  text, 
  language = 'de', 
  onWordHighlight,
  className = '',
  highlightColor = 'bg-[#4ECDC4]' 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(1.0);
  const [showControls, setShowControls] = useState(false);
  const [words, setWords] = useState([]);
  const [sentences, setSentences] = useState([]);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(-1);
  const [syncMethod, setSyncMethod] = useState('enhanced'); // 'enhanced' or 'boundary'
  const [debugInfo, setDebugInfo] = useState('');
  const [textVisible, setTextVisible] = useState(false); // Text wird erst nach erstem Klick auf Abspielen angezeigt
  
  const utteranceRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const startTimeRef = useRef(null);

  // Cleanup beim Komponentenabbau - KRITISCH für das Audio-Problem
  useEffect(() => {
    return () => {
      // Stoppe alle laufenden Audio-Instanzen beim Komponentenabbau
      if ('speechSynthesis' in window) {
        speechSynthesis.cancel();
      }
      
      // Stoppe alle Timer
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      
      // Reset states
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentWordIndex(-1);
      setProgress(0);
    };
  }, []);
  const boundarySupported = useRef(false);
  const { user } = useAuth();
  const { toast } = useToast();

  // Text in Sätze und Wörter aufteilen für bessere Synchronisation
  useEffect(() => {
    if (!text) return;

    // Text in vollständige Sätze aufteilen (inklusive Satzzeichen)
    const sentences = [];
    const sentenceParts = text.split(/([.!?]+)/);
    
    for (let i = 0; i < sentenceParts.length; i += 2) {
      const sentenceText = sentenceParts[i]?.trim();
      const punctuation = sentenceParts[i + 1] || '';
      
      if (sentenceText) {
        const fullSentence = sentenceText + punctuation;
        
        // Wörter und Satzzeichen für diesen Satz extrahieren (Deutsche Umlaute korrekt behandeln)
        const tokens = sentenceText.match(/[\wßäöüÄÖÜ]+|[^\w\s]/g) || [];
        const wordsInSentence = [];
        
        tokens.forEach((token, tokenIndex) => {
          wordsInSentence.push({
            word: token,
            index: tokenIndex,
            isPunctuation: /^[^\w\s]$/.test(token)
          });
        });
        
        // Satzzeichen am Ende hinzufügen
        if (punctuation) {
          wordsInSentence.push({
            word: punctuation,
            index: tokens.length,
            isPunctuation: true,
            isSentenceEnd: true
          });
        }
        
        sentences.push({
          text: fullSentence,
          words: wordsInSentence,
          index: sentences.length
        });
      }
    }
    
    setSentences(sentences);
    
    // Alle Wörter für globale Zählung
    const allWords = [];
    sentences.forEach(sentence => {
      sentence.words.forEach(wordObj => {
        allWords.push({
          ...wordObj,
          sentenceIndex: sentence.index,
          globalIndex: allWords.length
        });
      });
    });
    
    setWords(allWords);

    // Test ob Boundary Events unterstützt werden
    if ('speechSynthesis' in window) {
      try {
        const testUtterance = new SpeechSynthesisUtterance('test');
        boundarySupported.current = typeof testUtterance.onboundary !== 'undefined';
        setDebugInfo(`Boundary Events: ${boundarySupported.current ? 'Unterstützt' : 'Nicht unterstützt'}`);
      } catch (e) {
        boundarySupported.current = false;
        setDebugInfo('Boundary Events: Fehler bei Test');
      }
    }
  }, [text, language]);

  const getLanguageCode = (lang) => {
    const codes = {
      de: 'de-DE',
      en: 'en-US', 
      fr: 'fr-FR',
      tr: 'tr-TR'
    };
    return codes[lang] || 'de-DE';
  };

  // Präzise Wort-Synchronisation basierend auf Sätzen
  const updateWordHighlightBySentence = useCallback(() => {
    if (currentSentenceIndex < 0 || currentSentenceIndex >= sentences.length) return;
    
    const currentSentence = sentences[currentSentenceIndex];
    const sentenceWords = currentSentence
      .replace(/[^\w\sßäöüÄÖÜ]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 0);
    
    // Einfache zeitbasierte Progression innerhalb des Satzes
    const elapsedInSentence = (Date.now() - startTimeRef.current) / 1000;
    const avgWordDuration = (sentenceWords.length * 0.6) / speed; // Geschätzte Satzzeit
    const wordProgress = (elapsedInSentence / avgWordDuration) * sentenceWords.length;
    const currentWordInSentence = Math.floor(wordProgress);
    
    // Finde globalen Wort-Index
    const globalWordIndex = words.findIndex(w => 
      w.sentenceIndex === currentSentenceIndex && 
      w.localIndex === Math.min(currentWordInSentence, sentenceWords.length - 1)
    );
    
    if (globalWordIndex !== -1 && globalWordIndex !== currentWordIndex) {
      setCurrentWordIndex(globalWordIndex);
      if (onWordHighlight) {
        onWordHighlight(words[globalWordIndex].word, globalWordIndex);
      }
    }
  }, [currentSentenceIndex, sentences, words, currentWordIndex, speed, onWordHighlight]);

  // Satzweise TTS-Synthese für präzise Synchronisation
  const startContinuousSync = useCallback(() => {
    if (sentences.length === 0) return;
    
    let currentSentenceIndex = 0;
    let currentWordIndex = 0;
    let totalWordsProcessed = 0;
    
    // Berechne Gesamtzahl der Wörter für Progress
    const totalActualWords = sentences.reduce((count, sentence) => {
      return count + sentence.words.filter(w => !w.isPunctuation).length;
    }, 0);
    
    const playSentence = (sentenceIndex) => {
      if (sentenceIndex >= sentences.length) {
        // Ende erreicht
        setIsPlaying(false);
        setIsPaused(false);
        setCurrentWordIndex(-1);
        setCurrentSentenceIndex(-1);
        setProgress(100);
        
        toast({
          title: "Wiedergabe beendet",
          description: "Text vollständig abgespielt",
        });
        return;
      }
      
      const sentence = sentences[sentenceIndex];
      const actualWordsInSentence = sentence.words.filter(w => !w.isPunctuation);
      
      if (actualWordsInSentence.length === 0) {
        // Satz ohne Wörter, weiter zum nächsten
        playSentence(sentenceIndex + 1);
        return;
      }
      
      // Erstelle TTS nur für diesen Satz
      const sentenceText = actualWordsInSentence.map(w => w.word).join(' ');
      const utterance = new SpeechSynthesisUtterance(sentenceText);
      utterance.lang = getLanguageCode(language);
      utterance.rate = speed;
      utterance.volume = 1;
      
      // Versuche hochwertige Stimme zu bekommen
      const voices = speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.lang.startsWith(getLanguageCode(language)) && 
        (voice.name.includes('Neural') || voice.name.includes('Premium'))
      ) || voices.find(voice => voice.lang.startsWith(getLanguageCode(language)));
      
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
      
      // Wort-Tracking für diesen Satz
      const wordsPerMinute = language === 'de' ? 140 : 150;
      const wordsPerSecond = (wordsPerMinute / 60) * speed;
      const sentenceDuration = (actualWordsInSentence.length / wordsPerSecond) * 1000;
      const wordInterval = sentenceDuration / actualWordsInSentence.length;
      
      let wordIndexInSentence = 0;
      let wordTimer;
      
      const startWordTracking = () => {
        setCurrentSentenceIndex(sentenceIndex);
        
        wordTimer = setInterval(() => {
          if (wordIndexInSentence < actualWordsInSentence.length) {
            const targetWord = actualWordsInSentence[wordIndexInSentence];
            const globalIndex = words.findIndex(w => 
              w.sentenceIndex === sentenceIndex && 
              w.index === targetWord.index &&
              !w.isPunctuation
            );
            
            if (globalIndex !== -1) {
              setCurrentWordIndex(globalIndex);
              
              if (onWordHighlight) {
                onWordHighlight(words[globalIndex].word, globalIndex);
              }
              
              // Update progress
              const progressPercent = ((totalWordsProcessed + wordIndexInSentence + 1) / totalActualWords) * 100;
              setProgress(Math.min(progressPercent, 100));
            }
            
            wordIndexInSentence++;
          } else {
            clearInterval(wordTimer);
          }
        }, wordInterval);
        
        return wordTimer;
      };
      
      utterance.onend = () => {
        if (wordTimer) clearInterval(wordTimer);
        totalWordsProcessed += actualWordsInSentence.length;
        
        // Weiter zum nächsten Satz
        setTimeout(() => {
          playSentence(sentenceIndex + 1);
        }, 100); // Kurze Pause zwischen Sätzen
      };
      
      utterance.onerror = (event) => {
        if (wordTimer) clearInterval(wordTimer);
        setIsPlaying(false);
        setIsPaused(false);
        
        toast({
          title: "Audio-Fehler",
          description: "Die Sprachausgabe konnte nicht gestartet werden",
          variant: "destructive"
        });
      };
      
      // Store references for pause/resume
      utteranceRef.current = utterance;
      
      // Start word tracking immediately
      wordTimer = startWordTracking();
      progressIntervalRef.current = wordTimer;
      
      speechSynthesis.speak(utterance);
    };
    
    // Starte mit dem ersten Satz
    playSentence(0);
    
  }, [sentences, words, language, speed, onWordHighlight, toast]);

  const stopProgressTracking = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const handlePlay = async () => {
    if (!text.trim() || words.length === 0) return;

    // Text beim ersten Klick auf Abspielen sichtbar machen
    if (!textVisible) {
      setTextVisible(true);
    }

    if (isPaused && utteranceRef.current) {
      // Resume
      if ('speechSynthesis' in window) {
        speechSynthesis.resume();
        setIsPlaying(true);
        setIsPaused(false);
        
        // Resume word tracking - restart the synchronization from current position
        startContinuousSync();
      }
      return;
    }

    // Start new playback
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel(); // Cancel any existing speech
      
      // Clear any existing timers
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      
      setProgress(0);
      
      // Set playing state immediately
      setIsPlaying(true);
      setIsPaused(false);
      
      // Start continuous synchronization
      startContinuousSync();
      
      toast({
        title: "Synchronisierte Wiedergabe gestartet", 
        description: "Kontinuierliche Wort-Synchronisation aktiv",
      });
    }
  };

  const handlePause = () => {
    if ('speechSynthesis' in window) {
      speechSynthesis.pause();
      setIsPlaying(false);
      setIsPaused(true);
      
      // Stop word tracking completely during pause
      stopProgressTracking();
    }
  };

  const handleStop = useCallback(() => {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
    
    stopProgressTracking();
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentWordIndex(-1);
    setCurrentSentenceIndex(-1);
    setProgress(0);
    
    // Zusätzliche Sicherheit: Alle Audio-Instanzen stoppen
    if (utteranceRef.current) {
      utteranceRef.current = null;
    }
  }, []);

  // Automatisches Stoppen bei Seitenwechsel (useEffect für beforeunload)
  useEffect(() => {
    const handleBeforeUnload = () => {
      handleStop();
    };

    const handleVisibilityChange = () => {
      if (document.hidden && isPlaying) {
        handleStop();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      handleStop(); // Stoppe auch beim Component Unmount
    };
  }, [isPlaying, handleStop]);

  const renderSynchronizedText = () => {
    // Text nur anzeigen, wenn der User bereits auf Abspielen geklickt hat
    if (!textVisible) {
      return (
        <div className="text-lg leading-relaxed text-slate-800 text-center py-8">
          <p className="text-slate-500 italic">
            Klicken Sie auf "Abspielen", um den Text zu sehen und die Audio-Wiedergabe zu starten.
          </p>
        </div>
      );
    }

    if (sentences.length === 0) {
      return <p className="text-lg leading-relaxed text-slate-800">{text}</p>;
    }

    return (
      <div className="text-lg leading-relaxed text-slate-800 space-y-4">
        {sentences.map((sentence, sentenceIndex) => (
          <div key={sentenceIndex} className="mb-4">
            {sentence.words.map((wordObj, localIndex) => {
              // Globalen Index für dieses Wort finden
              const globalIndex = words.findIndex(w => 
                w.sentenceIndex === sentenceIndex && w.index === wordObj.index
              );
              
              const isCurrentWord = globalIndex === currentWordIndex;
              const isPassed = globalIndex < currentWordIndex && currentWordIndex >= 0;
              const isUpcoming = globalIndex > currentWordIndex && currentWordIndex >= 0;
              const isPunctuation = wordObj.isPunctuation;
              
              return (
                <span
                  key={`${sentenceIndex}-${localIndex}`}
                  className={`inline-block ${isPunctuation ? 'mr-1' : 'mr-2'} mb-1 ${
                    isPunctuation 
                      ? 'text-slate-800' // Satzzeichen werden nicht hervorgehoben
                      : `px-2 py-1 rounded transition-all duration-200 cursor-pointer ${
                          isCurrentWord
                            ? `${highlightColor} text-white shadow-lg scale-105 font-bold`
                            : isPassed
                            ? 'text-slate-400 opacity-70 bg-slate-100'
                            : isUpcoming
                            ? 'text-slate-700'
                            : 'text-slate-800 hover:bg-slate-100'
                        }`
                  }`}
                  onClick={() => {
                    // Nur bei Wörtern (nicht Satzzeichen) zu diesem Wort springen
                    if (!isPunctuation && 'speechSynthesis' in window) {
                      speechSynthesis.cancel();
                      
                      // Clear existing timer
                      if (progressIntervalRef.current) {
                        clearInterval(progressIntervalRef.current);
                      }
                      
                      // Update current position
                      setCurrentWordIndex(globalIndex);
                      
                      // Start from this position
                      startContinuousSync();
                    }
                  }}
                >
                  {wordObj.word}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Audio Controls */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg border">
        <div className="flex items-center space-x-3">
          <Button
            variant={isPlaying ? "default" : "outline"}
            size="sm"
            onClick={isPlaying ? handlePause : handlePlay}
            disabled={!text.trim()}
            className="min-w-[100px]"
          >
            {isPlaying ? (
              <>
                <Pause className="h-4 w-4 mr-2" />
                Pause
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                {isPaused ? 'Fortsetzen' : 'Abspielen'}
              </>
            )}
          </Button>

          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleStop}
            disabled={!isPlaying && !isPaused}
            className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Stopp
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowControls(!showControls)}
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center space-x-4 flex-1 mx-4">
          <div className="flex-1 bg-slate-200 rounded-full h-2 relative overflow-hidden">
            <div 
              className="bg-[#4ECDC4] h-2 rounded-full transition-all duration-200 relative"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute right-0 top-0 w-1 h-full bg-white opacity-70 animate-pulse"></div>
            </div>
          </div>
          <span className="text-sm text-slate-600 min-w-[60px]">
            {Math.round(progress)}%
          </span>
        </div>

        {/* Word Counter */}
        <div className="text-sm text-slate-600">
          {currentWordIndex >= 0 ? (
            <span>
              Wort {currentWordIndex + 1} von {words.length}
            </span>
          ) : (
            <span>{words.length} Wörter</span>
          )}
        </div>
      </div>

      {/* Advanced Controls */}
      {showControls && (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4 space-y-4">
            <h4 className="font-medium text-slate-900">Erweiterte Einstellungen</h4>
            
            {/* Speed Control */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Sprechgeschwindigkeit</label>
                <span className="text-sm text-slate-600">{speed.toFixed(1)}x</span>
              </div>
              <Slider
                value={[speed]}
                onValueChange={(value) => setSpeed(value[0])}
                max={2}
                min={0.5}
                step={0.1}
                className="w-full"
              />
            </div>

            {/* Synchronization Info */}
            <div className="bg-gradient-to-r from-blue-50 to-green-50 p-3 rounded-lg border">
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-medium text-slate-900 text-sm flex items-center">
                  <Zap className="h-4 w-4 mr-2 text-[#4ECDC4]" />
                  Optimierte Synchronisation
                </h5>
                <Badge variant="outline" className="text-xs">
                  {syncMethod === 'enhanced' ? 'Satz-basiert' : 'Boundary-Events'}
                </Badge>
              </div>
              <p className="text-slate-700 text-xs mb-2">
                Präzise Wort-für-Wort Synchronisation mit satz-basierter Segmentierung für bessere Genauigkeit.
              </p>
              {debugInfo && (
                <p className="text-xs text-slate-500 font-mono">
                  Debug: {debugInfo}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Synchronized Text Display */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Reading Mode Indicator */}
            {(isPlaying || isPaused) && (
              <div className="flex items-center justify-between mb-4 p-3 bg-[#4ECDC4] bg-opacity-10 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Volume2 className="h-4 w-4 text-[#4ECDC4]" />
                  <span className="text-sm font-medium text-[#4ECDC4]">
                    Synchronisiertes Lesen aktiv
                  </span>
                </div>
                <div className="text-xs text-slate-600">
                  Klicken Sie auf Wörter zum Springen
                </div>
              </div>
            )}
            
            {/* Synchronized Text */}
            {renderSynchronizedText()}
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Reading Statistics */}
      {(isPlaying || isPaused || progress > 0) && (
        <Card className="border-0 shadow-lg bg-gradient-to-r from-slate-50 to-slate-100">
          <CardContent className="p-4">
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-lg font-bold text-[#4ECDC4]">
                  {currentWordIndex >= 0 ? currentWordIndex + 1 : 0}
                </div>
                <div className="text-xs text-slate-600">Aktuelles Wort</div>
              </div>
              <div>
                <div className="text-lg font-bold text-[#4ECDC4]">
                  {currentSentenceIndex >= 0 ? currentSentenceIndex + 1 : 0}/{sentences.length}
                </div>
                <div className="text-xs text-slate-600">Satz</div>
              </div>
              <div>
                <div className="text-lg font-bold text-[#4ECDC4]">
                  {speed.toFixed(1)}x
                </div>
                <div className="text-xs text-slate-600">Geschwindigkeit</div>
              </div>
              <div>
                <div className="text-lg font-bold text-[#4ECDC4]">
                  {Math.round((sentences.length * 10) / speed)}s
                </div>
                <div className="text-xs text-slate-600">Geschätzte Dauer</div>
              </div>
            </div>
            
            {/* Sync Status */}
            <div className="mt-3 flex items-center justify-center">
              <div className="flex items-center space-x-2 text-xs">
                <div className={`w-2 h-2 rounded-full ${
                  isPlaying ? 'bg-green-500 animate-pulse' : 'bg-slate-400'
                }`}></div>
                <span className="text-slate-600">
                  {isPlaying ? 'Synchronisation aktiv' : isPaused ? 'Pausiert' : 'Bereit'}
                </span>
                {boundarySupported.current && (
                  <Badge variant="outline" className="text-xs ml-2">
                    Präzise Synchronisation
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SynchronizedTextReader;